import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TransactionPage from './pages/TransactionPage';
import FrontOfficeTransactionBlotter from './pages/FrontOfficeTransactionBlotter';
import FrontOfficeGsecBlotter from './pages/FrontOfficeGsecBlotter';
import FrontOfficeMoneyMarketBlotter from './pages/FrontOfficeMoneyMarketBlotter';
import FrontOfficeBuybackBlotter from './pages/FrontOfficeBuybackBlotter';
import AuthorizerPage from './pages/AuthorizerPage';
import BackOfficeTransactionBlotter from './pages/BackOfficeTransactionBlotter';
import BackOfficeFinalAuthPage from './pages/BackOfficeFinalAuthPage';
import BackOfficeVerifierGsecBlotter from './pages/BackOfficeVerifierGsecBlotter';
import BackOfficeFinalVerifierGsecBlotter from './pages/BackOfficeFinalVerifierGsecBlotter';
import FrontOfficeTbillBlotter from './pages/FrontOfficeTbillBlotter';
import BackOfficeVerifierTbillBlotter from './pages/BackOfficeVerifierTbillBlotter';
import BackOfficeFinalVerifierTbillBlotter from './pages/BackOfficeFinalVerifierTbillBlotter';
import BackOfficeVerifierBuybackBlotter from './pages/BackOfficeVerifierBuybackBlotter';
import BackOfficeFinalBuybackBlotter from './pages/BackOfficeFinalBuybackBlotter';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import UserManagementPage from './pages/UserManagementPage';
import AdminPage from './pages/AdminPage';
import SettlementPreviewPage from './pages/SettlementPreviewPage';
import AdminAuthorizerPanel from './pages/AdminAuthorizerPanel';
import MiddleOfficeAccessPanel from './pages/MiddleOfficeAccessPanel';
import DealerLimitSetupPage from './pages/DealerLimitSetupPage';
import IsinMasterPage from './pages/IsinMasterPage';
import FixedIncomeGsecPage from './pages/FixedIncomeGsecPage';
import FixedIncomeTbillPage from './pages/FixedIncomeTbillPage';
import FixedIncomeBuyBackPage from './pages/FixedIncomeBuyBackPage';
import GsecReportPage from './pages/GsecReportPage';
import TbillReportPage from './pages/TbillReportPage';
import GsecSummaryReportPage from './pages/GsecSummaryReportPage';
import PortfolioReportPage from './pages/PortfolioReportPage';
import CounterpartyReportPage from './pages/CounterpartyReportPage';
import CounterpartyMasterPage from './pages/CounterpartyMasterPage';
import LimitSetupPage from './pages/LimitSetupPage';
import CounterpartyLimitRequestBlotter from './pages/CounterpartyLimitRequestBlotter';
import PortfolioMasterPage from './pages/PortfolioMasterPage';
import IssuerMasterPage from './pages/IssuerMasterPage';
import InvestmentApproverMasterPage from './pages/InvestmentApproverMasterPage';
import BrokerMasterPage from './pages/BrokerMasterPage';
import StrategyMasterPage from './pages/StrategyMasterPage';
import PaymentMasterPage from './pages/PaymentMasterPage';
import MoneyMarketDealPage from './pages/MoneyMarketDealPage';
import FixedDepositPage from './pages/FixedDepositPage';
import DebenturePage from './pages/DebenturePage';
import SettlementAccountMasterPage from './pages/SettlementAccountMasterPage';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import SystemStatusBar from './components/SystemStatusBar';
import SideNav from './components/SideNav';
import AuthorizerSideNav from './components/AuthorizerSideNav';
import PageHeaderBanner from './components/PageHeaderBanner';
import { Toaster } from 'sonner'; // Sonner toast provider
import { ToastContainer } from 'react-toastify'; // React Toastify provider
import BackOfficeMoneyMarketBlotter from './pages/BackOfficeMoneyMarketBlotter';
import BackOfficeFinalMoneyMarketBlotter from './pages/BackOfficeFinalMoneyMarketBlotter';
import BackOfficeFinalFixedDepositBlotter from './pages/BackOfficeFinalFixedDepositBlotter';
import EODPage from './pages/EODPage';
import MaturityPage from './pages/MaturityPage';
import MaturityFrontOfficeBlotter from './pages/MaturityFrontOfficeBlotter';
import MaturityBackOfficeVerifierBlotter from './pages/MaturityBackOfficeVerifierBlotter';
import MaturityBackOfficeFinalBlotter from './pages/MaturityBackOfficeFinalBlotter';
import CouponMaturityBlotter from './pages/CouponMaturityBlotter';
import DailyDealBlotter from './pages/DailyDealBlotter';
import DailyTransactionBlotter from './pages/DailyTransactionBlotter';
import RepoPage from './pages/RepoPage';
import MarkToMarketPage from './pages/MarkToMarketPage';
import MarkToMarketReportPage from './pages/MarkToMarketReportPage';
import BuybackReportPage from './pages/BuybackReportPage';
import BrokerReportPage from './pages/BrokerReportPage';
import DailyCashflowBlotterPage from './pages/DailyCashflowBlotterPage';
import SellTransactionReportPage from './pages/SellTransactionReportPage';
import RepoReportPage from './pages/RepoReportPage';
import HolidayCalendarPage from './pages/HolidayCalendarPage';
import FundCentreMasterPage from './pages/FundCentreMasterPage';
import RepoAuthorizationBlotter from './pages/RepoAuthorizationBlotter';
import VouchersPage from './pages/VouchersPage';
import InterestIncomeReportPage from './pages/InterestIncomeReportPage';

const ALL_TABS = [
  { key: 'transactions', label: 'Transactions', path: '/transactions' },
  { key: 'eod', label: 'EOD', path: '/eod' },
  { key: 'payment_master', label: 'Payment Master', path: '/payment-master' },
  { key: 'isin_master', label: 'ISIN Master', path: '/isin-master' },
  { key: 'counterparty_master', label: 'Counterparty Master', path: '/counterparty-master' },
  { key: 'limit_setup', label: 'Limit Setup', path: '/limit-setup' },
  { key: 'fixed_income_gsec', label: 'Fixed Income Gsec', path: '/back-office-verifier-gsec-blotter' },
  { key: 'fixed_income_others', label: 'Fixed Income Others', path: '/fixed-income-others' },
  { key: 'money_market', label: 'Money Market', path: '/money-market' }, // <-- Added Money Market tab
  { key: 'buyback', label: 'Buyback', path: '/fixed-income-buy-back' },
  { key: 'maturity', label: 'Maturity Handling', path: '/maturity' },
  { key: 'maturity_authorization', label: 'Maturity Authorization', path: '/maturity-front-office-blotter' },
  { key: 'payments_rtgs', label: 'Payments - RTGS', path: '/payments-rtgs' },
  { key: 'payments_other', label: 'Payments - Other', path: '/payments-other' },
  { key: 'repo', label: 'Repo', path: '/repo' },
  { key: 'reverse_repo', label: 'Mark to Market', path: '/mark-to-market' },
  { key: 'portfolio_master', label: 'Portfolio Master', path: '/portfolio-master' },
  { key: 'broker_master', label: 'Broker Master', path: '/broker-master' },
  { key: 'strategy_master', label: 'Strategy Master', path: '/strategy-master' },
  { key: 'investment_approver_master', label: 'Investment Approver Master', path: '/investment-approver-master' },
  { key: 'issuer_master', label: 'Issuer Master', path: '/issuer-master' },
  { key: 'daily-deal-blotter', label: 'Daily Deal Blotter', path: '/daily-deal-blotter' },
  { key: 'daily-cashflow-blotter', label: 'Daily Cashflow Blotter', path: '/daily-cashflow-blotter' },
  { key: 'daily-transaction-blotter', label: 'Daily Transaction Blotter', path: '/daily-transaction-blotter' },
  { key: 'admin_users', label: 'Admin - Users', path: '/admin/users' },
  { key: 'admin_accounting', label: 'Admin - Accounting', path: '/admin/accounting' },
  { key: 'bank_charges', label: 'Vouchers', path: '/bank-charges' },
  { key: 'interest_income_report', label: 'Interest Income Report', path: '/reports/interest-income' }
];

;

function getAllowedTabs() {
  const baseTabs = [
    { key: 'transactions', label: 'Transactions', path: '/transactions' },
    { key: 'payment_master', label: 'Payment Master', path: '/payment-master' },
    { key: 'isin_master', label: 'ISIN Master', path: '/isin-master' },
    { key: 'counterparty_master', label: 'Counterparty Master', path: '/counterparty-master' },
    { key: 'limit_setup', label: 'Limit Setup', path: '/limit-setup' },
    { key: 'fixed_income_gsec', label: 'Fixed Income Gsec', path: '/fixed-income-gsec' }, // default
    { key: 'fixed_income_tbill', label: 'Fixed Income T-Bill', path: '/fixed-income-tbill' },
    { key: 'fixed_income_others', label: 'Fixed Income Others', path: '/fixed-income-others' },
    { key: 'money_market', label: 'Money Market', path: '/money-market' },
    { key: 'fixed_deposit', label: 'Fixed Deposit', path: '/fixed-deposit' },
    { key: 'debenture', label: 'Debenture', path: '/debenture' },
    { key: 'buyback', label: 'Buyback', path: '/fixed-income-buy-back' },
    { key: 'maturity', label: 'Maturity Handling', path: '/maturity' },
    { key: 'maturity_authorization', label: 'Maturity Authorization', path: '/maturity-front-office-blotter' },
    { key: 'money-market-report', label: 'Money Market Report', path: '/reports/money-market' },
    { key: 'mark-to-market-report', label: 'Mark to Market Report', path: '/reports/mark-to-market' },
    { key: 'repo-report', label: 'Repo / Reverse Repo Report', path: '/reports/repo' },
    { key: 'buyback-report', label: 'Buyback Report', path: '/reports/buyback' },
    { key: 'sell-transaction-report', label: 'Sell Transaction Report', path: '/reports/sell-transaction' },
    { key: 'broker-report', label: 'Broker Report', path: '/reports/broker' },
    { key: 'daily-cashflow-blotter', label: 'Daily Cashflow Blotter', path: '/daily-cashflow-blotter' },
    { key: 'portfolio-report', label: 'Portfolio Report', path: '/portfolio-report' },
    { key: 'counterparty-report', label: 'Counterparty Transaction Report', path: '/counterparty-report' },
    { key: 'coupon-maturity-blotter', label: 'Coupon Maturity Blotter', path: '/coupon-maturity-blotter' },
    { key: 'daily-deal-blotter', label: 'Daily Deal Blotter', path: '/daily-deal-blotter' },
    { key: 'daily-transaction-blotter', label: 'Daily Transaction Blotter', path: '/daily-transaction-blotter' },
    { key: 'payments_rtgs', label: 'Payments - RTGS', path: '/payments-rtgs' },
    { key: 'payments_other', label: 'Payments - Other', path: '/payments-other' },
    { key: 'repo', label: 'Repo', path: '/repo' },
    { key: 'reverse_repo', label: 'Mark to Market', path: '/mark-to-market' },
    { key: 'portfolio_master', label: 'Portfolio Master', path: '/portfolio-master' },
    { key: 'broker_master', label: 'Broker Master', path: '/broker-master' },
    { key: 'strategy_master', label: 'Strategy Master', path: '/strategy-master' },
    { key: 'investment_approver_master', label: 'Investment Approver Master', path: '/investment-approver-master' },
    { key: 'issuer_master', label: 'Issuer Master', path: '/issuer-master' },
    { key: 'admin_users', label: 'Admin - Users', path: '/admin/users' },
    { key: 'admin_accounting', label: 'Admin - Accounting', path: '/admin/accounting' },
    { key: 'admin_settlement_preview', label: 'Settlement Preview', path: '/admin/settlement-preview' },
    { key: 'settlement_account_master', label: 'Settlement Account Master', path: '/admin/settlement-account-master' },
    { key: 'holiday_calendar', label: 'Holiday Calendar', path: '/holiday-calendar' },
    { key: 'fund_centre_master', label: 'Fund Centre Master', path: '/fund-centre-master' },
    { key: 'eod', label: 'EOD', path: '/eod' },
    { key: 'gsec_report', label: 'T Bond Report', path: '/gsec-report' }, // <-- Added here
    { key: 'tbill_report', label: 'T-Bill Report', path: '/tbill-report' },
    { key: 'gsec_summary_report', label: 'T Bond Summary Report', path: '/gsec-summary-report' },
    { key: 'bank_charges', label: 'Vouchers', path: '/bank-charges' },
    { key: 'interest_income_report', label: 'Interest Income Report', path: '/reports/interest-income' }
  ];

  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userData.role === 'admin';
  
  // Filter out admin tabs for non-admin users, except the accounting ones, which an
  // admin can grant per user from User Management. admin_users stays admin-only.
  const USER_ASSIGNABLE_ADMIN_TABS = ['admin_accounting', 'admin_settlement_preview'];
  const filteredTabs = isAdmin 
    ? baseTabs 
    : baseTabs.filter(tab => (!tab.key.startsWith('admin_') || USER_ASSIGNABLE_ADMIN_TABS.includes(tab.key)) && tab.key !== 'eod');
  
  console.log('Navigation Debug:', { 
    isAdmin, 
    totalTabs: baseTabs.length, 
    filteredTabs: filteredTabs.length,
    adminTabs: baseTabs.filter(tab => tab.key.startsWith('admin_') || tab.key === 'eod').map(t => t.key)
  });
  
  let allowedTabKeys = isAdmin
    ? filteredTabs.map(t => t.key)
    : (userData.allowed_tabs || []);
  if (userData.role === 'limits_allocating_user' || userData.role === 'limits_allocating_authorizer') {
    allowedTabKeys = ['limit_setup'];
  }

  // These pages are gated on a middle-office ASSIGNMENT (see their routes'
  // assignmentRolePrefix), not on allowed_tabs. Users carrying a stale
  // allowed_tabs grant would otherwise still be shown the nav link and get
  // bounced when they clicked it, so filter them out of the menu to match.
  // Counterparty Master is deliberately NOT in this list - it uses plain
  // allowed_tabs gating, so anyone granted the tab may view and edit it.
  const MIDDLE_OFFICE_ONLY_TABS = ['limit_setup'];
  const hasMiddleOfficeAssignment = Array.isArray(userData.assignments) && userData.assignments.some(
    (a) => typeof a.role === 'string' && a.role.startsWith('middle_office_')
  );
  if (!isAdmin && !hasMiddleOfficeAssignment) {
    allowedTabKeys = allowedTabKeys.filter((k) => !MIDDLE_OFFICE_ONLY_TABS.includes(k));
  }

  // Dynamically adjust the path for fixed_income_gsec based on role
  const tabs = filteredTabs.map(tab => {
    if (tab.key === 'fixed_income_gsec') {
      if (userData.role === 'back_office_final') {
        return { ...tab, path: '/back-office-final-verifier-gsec-blotter' };
      }
      if (userData.role === 'back_office_verifier') {
        return { ...tab, path: '/back-office-verifier-gsec-blotter' };
      }
    }
    if (tab.key === 'fixed_income_tbill') {
      if (userData.role === 'back_office_final') {
        return { ...tab, path: '/back-office-final-verifier-tbill-blotter' };
      }
      if (userData.role === 'back_office_verifier') {
        return { ...tab, path: '/back-office-verifier-tbill-blotter' };
      }
    }
    if (tab.key === 'buyback') {
      if (userData.role === 'back_office_final') {
        return { ...tab, path: '/back-office-final-buyback-blotter' };
      }
      if (userData.role === 'back_office_verifier') {
        return { ...tab, path: '/back-office-verifier-buyback-blotter' };
      }
      if (userData.role === 'front_office_verifier' || userData.role === 'front_office') {
        return { ...tab, path: '/front-office-buyback-blotter' };
      }
    }
    if (tab.key === 'money_market') {
      if (userData.role === 'back_office_final') {
        return { ...tab, path: '/back-office-final-money-market-blotter' };
      }
      if (userData.role === 'back_office_verifier') {
        return { ...tab, path: '/back-office-money-market-blotter' };
      }
      if (userData.role === 'front_office_verifier' || userData.role === 'front_office') {
        return { ...tab, path: '/front-office-money-market-blotter' };
      }
    }
    // Maturity Handling (`maturity`) always opens /maturity (Daily Maturity Cashflow).
    // FO/BO authorization blotters use the separate `maturity_authorization` tab key.
    if (tab.key === 'maturity_authorization') {
      if (userData.role === 'back_office_final') {
        return { ...tab, path: '/maturity-back-office-final-blotter' };
      }
      if (userData.role === 'back_office_verifier') {
        return { ...tab, path: '/maturity-back-office-verifier-blotter' };
      }
      if (userData.role === 'front_office_verifier' || userData.role === 'front_office') {
        return { ...tab, path: '/maturity-front-office-blotter' };
      }
    }
    if (tab.key === 'repo') {
      if (userData.role === 'back_office_final' || userData.role === 'back_office_verifier' || userData.role === 'front_office_verifier' || userData.role === 'front_office') {
        return { ...tab, path: '/repo-authorization-blotter' };
      }
    }
    return tab;
  });

  return tabs.filter(tab => allowedTabKeys.includes(tab.key));
}



// Route to page title mapping
const getPageTitle = (pathname) => {
  const titleMap = {
    '/fixed-income-gsec': 'Fixed Income Gsec - Bond',
    '/fixed-income-tbill': 'Fixed Income T-Bill',
    '/fixed-income-buy-back': 'Fixed Income Buyback',
    '/fixed-deposit': 'Fixed Deposit',
    '/debenture': 'Debenture',
    '/money-market': 'Money Market Deal',
    '/transactions': 'Transactions',
    '/payment-master': 'Payment Master',
    '/isin-master': 'ISIN Master',
    '/counterparty-master': 'Counterparty Master',
    '/limit-setup': 'Limit Setup',
    '/portfolio-master': 'Portfolio Master',
    '/broker-master': 'Broker Master',
    '/strategy-master': 'Strategy Master',
    '/investment-approver-master': 'Investment Approver Master',
    '/issuer-master': 'Issuer Master',
    '/admin/users': 'User Management',
    '/admin/accounting': 'Admin - Accounting',
    '/admin/settlement-preview': 'Settlement Ledger Preview',
    '/admin/authorizers': 'Admin - Authorizers',
    '/admin/middle-office-access': 'Middle Office Access',
    '/admin/dealer-limits': 'Dealer Limit Setup',
    '/admin/settlement-account-master': 'Settlement Account Master',
    '/holiday-calendar': 'Holiday Calendar',
    '/fund-centre-master': 'Fund Centre Master',
    '/gsec-report': 'T Bond Product Report',
    '/tbill-report': 'T-Bill Product Report',
    '/gsec-summary-report': 'T Bond Summary Report (ISIN-wise)',
    '/portfolio-report': 'Portfolio Report',
    '/counterparty-report': 'Counterparty Transaction Report',
    '/repo': 'Repo Deal',
    '/mark-to-market': 'Mark to Market',
    '/maturity': 'Maturity Management',
    '/daily-deal-blotter': 'Daily Deal Blotter',
    '/daily-cashflow-blotter': 'Daily Cashflow Blotter',
    '/daily-transaction-blotter': 'Daily Transaction Blotter',
    '/eod': 'End of Day Processing',
    '/authorizer': 'Authorizer Panel',
    '/back-office-transaction-blotter': 'Back Office Transaction Blotter',
    '/front-office-transaction-blotter': 'Front Office Transaction Blotter',
    '/back-office-verifier-gsec-blotter': 'Back Office Verifier - GSec Blotter',
    '/back-office-final-verifier-gsec-blotter': 'Back Office Final Verifier - GSec Blotter',
    '/back-office-verifier-tbill-blotter': 'Back Office Verifier - T-Bill Blotter',
    '/back-office-final-verifier-tbill-blotter': 'Back Office Final Verifier - T-Bill Blotter',
    '/front-office-buyback-blotter': 'Front Office Buyback Blotter',
    '/back-office-verifier-buyback-blotter': 'Back Office Verifier - Buyback Blotter',
    '/back-office-final-buyback-blotter': 'Back Office Final - Buyback Blotter',
    '/front-office-money-market-blotter': 'Front Office Money Market Blotter',
    '/back-office-money-market-blotter': 'Back Office Money Market Blotter',
    '/back-office-final-money-market-blotter': 'Back Office Final Money Market Blotter',
    '/back-office-final-fixed-deposit-blotter': 'Back Office Final Fixed Deposit Blotter',
    '/backoffice-final-auth': 'Back Office Final Authorization',
    '/reports/sell-transaction': 'Sell Transaction Report',
    '/reports/buyback': 'Buyback Report',
    '/reports/mark-to-market': 'Mark to Market Report',
  };
  
  return titleMap[pathname] || '';
};

// Main App layout component with location awareness
function AppLayout({ onLogin }) {
  const location = useLocation();
  const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password'];
  const isPublicPage = PUBLIC_ROUTES.includes(location.pathname);
  const pageTitle = getPageTitle(location.pathname);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthorizer = user.role === 'authorizer';
  const shouldShowSideNav = !isPublicPage;

  return (
    <div className="App" style={{ display: 'flex' }}>
      {shouldShowSideNav && (
        isAuthorizer ? (
          <AuthorizerSideNav />
        ) : (
          <SideNav allowedTabs={getAllowedTabs()} />
        )
      )}
      <div className={`app-main${isPublicPage ? ' app-main--public' : ''}`}>
        {!isPublicPage && pageTitle && (
          <PageHeaderBanner title={pageTitle} />
        )}

        <Routes>
          <Route path="/back-office-transaction-blotter" element={<BackOfficeTransactionBlotter />} />
          <Route path="/front-office-money-market-blotter" element={
            <ProtectedRoute roles={["front_office_verifier", "front_office"]}>
              <FrontOfficeMoneyMarketBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-money-market-blotter" element={
            <ProtectedRoute roles={["back_office_verifier"]}>
              <BackOfficeMoneyMarketBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-verifier-gsec-blotter" element={
            <ProtectedRoute roles={["back_office_verifier"]}>
              <BackOfficeVerifierGsecBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-final-verifier-gsec-blotter" element={
            <ProtectedRoute roles={["back_office_final"]}>
              <BackOfficeFinalVerifierGsecBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-verifier-tbill-blotter" element={
            <ProtectedRoute roles={["back_office_verifier"]}>
              <BackOfficeVerifierTbillBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-final-verifier-tbill-blotter" element={
            <ProtectedRoute roles={["back_office_final"]}>
              <BackOfficeFinalVerifierTbillBlotter />
            </ProtectedRoute>
          } />
          <Route path="/front-office-buyback-blotter" element={
            <ProtectedRoute roles={["front_office_verifier", "front_office"]}>
              <FrontOfficeBuybackBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-verifier-buyback-blotter" element={
            <ProtectedRoute roles={["back_office_verifier"]}>
              <BackOfficeVerifierBuybackBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-final-buyback-blotter" element={
            <ProtectedRoute roles={["back_office_final"]}>
              <BackOfficeFinalBuybackBlotter />
            </ProtectedRoute>
          } />
          <Route path="/repo-authorization-blotter" element={
            <ProtectedRoute roles={["front_office_verifier", "front_office", "back_office_verifier", "back_office_final", "admin"]}>
              <RepoAuthorizationBlotter />
            </ProtectedRoute>
          } />
          <Route path="/backoffice-final-auth" element={
            <ProtectedRoute roles={['back_office_final']}>
              <BackOfficeFinalAuthPage />
            </ProtectedRoute>
          } />
          <Route path="/login" element={<LoginPage onLogin={onLogin} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={
            <ProtectedRoute roles={['user', 'admin', 'authorizer', 'limits_allocating_user', 'limits_allocating_authorizer', 'front_office', 'front_office_verifier', 'back_office', 'back_office_verifier', 'back_office_final', 'tt']}>
              <ChangePasswordPage />
            </ProtectedRoute>
          } />
          <Route path="/transactions" element={
            <ProtectedRoute roles={['user', 'authorizer', 'admin']}>
              <TransactionPage allowedTabs={getAllowedTabs()} />
            </ProtectedRoute>
          } />
          <Route path="/authorizer" element={
            <ProtectedRoute roles="authorizer">
              <AuthorizerPage />
            </ProtectedRoute>
          } />
          <Route path="/front-office-transaction-blotter" element={
            <ProtectedRoute roles={["authorizer", "front_office"]}>
              <FrontOfficeTransactionBlotter />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute roles="admin">
              <UserManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/accounting" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="admin_accounting">
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/settlement-preview" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="admin_settlement_preview">
              <SettlementPreviewPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/authorizers" element={
            <ProtectedRoute roles="admin">
              <AdminAuthorizerPanel />
            </ProtectedRoute>
          } />
          <Route path="/admin/middle-office-access" element={
            <ProtectedRoute roles="admin">
              <MiddleOfficeAccessPanel />
            </ProtectedRoute>
          } />
          <Route path="/admin/dealer-limits" element={
            <ProtectedRoute assignmentRolePrefix="middle_office_">
              <DealerLimitSetupPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/settlement-account-master" element={
            <ProtectedRoute roles={['user', 'tt', 'admin', 'back_office_verifier', 'back_office_final']}>
              <SettlementAccountMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/holiday-calendar" element={
            <ProtectedRoute roles={['user', 'authorizer', 'admin', 'back_office_verifier', 'back_office_final']}>
              <HolidayCalendarPage />
            </ProtectedRoute>
          } />
          <Route path="/fund-centre-master" element={
            <ProtectedRoute roles={['user', 'authorizer', 'admin', 'back_office_verifier', 'back_office_final']}>
              <FundCentreMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/portfolio-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="portfolio_master">
              <PortfolioMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/investment-approver-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="investment_approver_master">
              <InvestmentApproverMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/issuer-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="issuer_master">
              <IssuerMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/broker-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="broker_master">
              <BrokerMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/strategy-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="strategy_master">
              <StrategyMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/isin-master" element={
            <ProtectedRoute roles={['user', 'authorizer', 'admin', 'back_office_verifier', 'back_office_final']}>
              <IsinMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/counterparty-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="counterparty_master">
              <CounterpartyMasterPage />
            </ProtectedRoute>
          } />
          <Route path="/fixed-income-gsec" element={
            (() => {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              if (user.role === 'front_office_verifier' || user.role === 'front_office') {
                return <FrontOfficeGsecBlotter />;
              }
              if (user.role === 'back_office_verifier') {
                return (
                  <ProtectedRoute roles={['back_office_verifier']}>
                    <BackOfficeVerifierGsecBlotter />
                  </ProtectedRoute>
                );
              }
              if (user.role === 'back_office_final') {
                return (
                  <ProtectedRoute roles={['back_office_final']}>
                    <BackOfficeFinalVerifierGsecBlotter />
                  </ProtectedRoute>
                );
              }
              return (
                <ProtectedRoute roles={['user', 'authorizer', 'admin']}>
                  <FixedIncomeGsecPage allowedTabs={getAllowedTabs()} />
                </ProtectedRoute>
              );
            })()
          } />
          <Route path="/fixed-income-tbill" element={
            (() => {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              if (user.role === 'front_office_verifier' || user.role === 'front_office') {
                return <FrontOfficeTbillBlotter />;
              }
              return (
                <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="fixed_income_tbill">
                  <FixedIncomeTbillPage allowedTabs={getAllowedTabs()} />
                </ProtectedRoute>
              );
            })()
          } />
          <Route path="/fixed-income-buy-back" element={<FixedIncomeBuyBackPage />} />
          <Route path="/money-market" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="money_market">
              <MoneyMarketDealPage allowedTabs={getAllowedTabs()} />
            </ProtectedRoute>
          } />
          {/* Fixed Deposit Pre-Approval Screen */}
          <Route path="/fixed-deposit" element={
            <ProtectedRoute roles={['user', 'authorizer', 'admin', 'front_office', 'front_office_verifier', 'back_office_verifier', 'back_office_final']}>
              <FixedDepositPage />
            </ProtectedRoute>
          } />
          {/* Debenture Investment Screen */}
          <Route path="/debenture" element={
            <ProtectedRoute roles={['user', 'authorizer', 'admin', 'front_office', 'front_office_verifier', 'back_office_verifier', 'back_office_final']}>
              <DebenturePage />
            </ProtectedRoute>
          } />
          <Route path="/payment-master" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="payment_master">
              <PaymentMasterPage allowedTabs={getAllowedTabs()} />
            </ProtectedRoute>
          } />
          <Route path="/limit-setup" element={
            <ProtectedRoute assignmentRolePrefix="middle_office_">
              <LimitSetupPage />
            </ProtectedRoute>
          } />
          <Route path="/counterparty-limit-requests" element={
            <ProtectedRoute assignmentRolePrefix="middle_office_">
              <CounterpartyLimitRequestBlotter />
            </ProtectedRoute>
          } />
          <Route path="/gsec-report" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="gsec_report">
              <GsecReportPage />
            </ProtectedRoute>
          } />
          <Route path="/tbill-report" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="tbill_report">
              <TbillReportPage />
            </ProtectedRoute>
          } />
          <Route path="/gsec-summary-report" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="gsec_summary_report">
              <GsecSummaryReportPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/mark-to-market" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="mark-to-market-report">
              <MarkToMarketReportPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/repo" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="repo-report">
              <RepoReportPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/buyback" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="buyback-report">
              <BuybackReportPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/broker" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="broker-report">
              <BrokerReportPage />
            </ProtectedRoute>
          } />
          <Route path="/daily-cashflow-blotter" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="daily-cashflow-blotter">
              <DailyCashflowBlotterPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/sell-transaction" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="sell-transaction-report">
              <SellTransactionReportPage />
            </ProtectedRoute>
          } />
          <Route path="/bank-charges" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="bank_charges">
              <VouchersPage />
            </ProtectedRoute>
          } />
          <Route path="/reports/interest-income" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="interest_income_report">
              <InterestIncomeReportPage />
            </ProtectedRoute>
          } />
          <Route path="/portfolio-report" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="portfolio-report">
              <PortfolioReportPage />
            </ProtectedRoute>
          } />
          <Route path="/counterparty-report" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="counterparty-report">
              <CounterpartyReportPage />
            </ProtectedRoute>
          } />
          <Route path="/maturity" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="maturity">
              <MaturityPage />
            </ProtectedRoute>
          } />
          <Route path="/repo" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="repo">
              <RepoPage />
            </ProtectedRoute>
          } />
          <Route path="/mark-to-market" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="reverse_repo">
              <MarkToMarketPage />
            </ProtectedRoute>
          } />

          <Route path="/eod" element={
            <ProtectedRoute roles={['admin']}>
              <EODPage />
            </ProtectedRoute>
          } />
          <Route path="/back-office-final-money-market-blotter" element={
            <ProtectedRoute roles={["back_office_final"]}>
              <BackOfficeFinalMoneyMarketBlotter />
            </ProtectedRoute>
          } />
          <Route path="/back-office-final-fixed-deposit-blotter" element={
            <ProtectedRoute roles={["back_office_final"]}>
              <BackOfficeFinalFixedDepositBlotter />
            </ProtectedRoute>
          } />
          <Route path="/maturity-front-office-blotter" element={
            <ProtectedRoute roles={["front_office_verifier", "front_office"]}>
              <MaturityFrontOfficeBlotter />
            </ProtectedRoute>
          } />
          <Route path="/maturity-back-office-verifier-blotter" element={
            <ProtectedRoute roles={["back_office_verifier"]}>
              <MaturityBackOfficeVerifierBlotter />
            </ProtectedRoute>
          } />
          <Route path="/maturity-back-office-final-blotter" element={
            <ProtectedRoute roles={["back_office_final"]}>
              <MaturityBackOfficeFinalBlotter />
            </ProtectedRoute>
          } />
          <Route path="/coupon-maturity-blotter" element={
            <ProtectedRoute>
              <CouponMaturityBlotter />
            </ProtectedRoute>
          } />
          <Route path="/daily-deal-blotter" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="daily-deal-blotter">
              <DailyDealBlotter />
            </ProtectedRoute>
          } />
          <Route path="/daily-transaction-blotter" element={
            <ProtectedRoute allowedTabs={getAllowedTabs()} tabKey="daily-transaction-blotter">
              <DailyTransactionBlotter />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in on component mount
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = (userData) => {
    console.log('Login successful:', userData);
    setIsLoggedIn(true);
    // No need to navigate here, LoginPage will handle navigation
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    // After logout, we'll redirect to login page in the return statement
  };

  return (
    <Router>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <>
          <Toaster position="top-right" richColors closeButton />
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
          <AppLayout onLogin={handleLogin} />
          <Toaster position="top-right" richColors />
          <SystemStatusBar />
        </>
      </LocalizationProvider>
    </Router>
  );
}

export default App;